import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cls from "app/utils/classNames";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import CloseIcon from "../../../../public/images/close-icon-dark.svg";
import styles from "./dialogs.module.scss";
import { ResourceCategoryResponse } from "app/utils/request/scheduling/facilityResources";

export type SectionModalMode = "create" | "rename";

// What the modal sends. `id` is present for a rename only. The modal asks for
// no kind: this version has bookable sections only, and the page creates each
// section with that kind.
export interface SectionValues {
  id?: number;
  title: string;
}

interface SectionModalProps {
  isOpen: boolean;
  mode: SectionModalMode;
  // The section to rename. The create mode ignores it.
  category: ResourceCategoryResponse | null;
  existingTitles: string[];
  onClose: () => void;
  // Rejects with the API errors when the save fails.
  onSave: (values: SectionValues) => Promise<void>;
}

// Takes the first message of each field from an API error, with a capital
// first letter, as the booking option modals do.
const fieldErrorsFrom = (error: any): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};
  Object.entries(error?.errors ?? {}).forEach(([key, messages]) => {
    if (Array.isArray(messages) && messages.length > 0) {
      const message = String(messages[0]);
      fieldErrors[key] = message.charAt(0).toUpperCase() + message.slice(1);
    }
  });

  return fieldErrors;
};

const SectionModal: React.FC<SectionModalProps> = ({
  isOpen,
  mode,
  category,
  existingTitles,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const isRename = mode === "rename";

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setName(isRename ? category?.title ?? "" : "");
    setServerErrors({});
  }, [isOpen, isRename, category]);

  const trimmed = name.trim();
  // A rename can keep the current title, so the check skips that title.
  const otherTitles = existingTitles.filter(
    (title) => !(isRename && title === category?.title)
  );
  const isDuplicate = otherTitles.some(
    (title) => title.toLowerCase() === trimmed.toLowerCase()
  );
  const nameError = isDuplicate
    ? t("This facility already has a section with that name.")
    : serverErrors.title || serverErrors.base;

  const handleSave = async () => {
    setIsSaving(true);
    setServerErrors({});
    try {
      await onSave({
        id: isRename ? category?.id : undefined,
        title: trimmed,
      });
    } catch (error) {
      setServerErrors(fieldErrorsFrom(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      classNames={cls(styles.modal, styles.modal_small)}
    >
      <div className={styles.modal_header}>
        <h2>{isRename ? t("Rename Section") : t("Add Section")}</h2>
        <button
          type="button"
          className={styles.modal_close}
          onClick={onClose}
          aria-label={t("Close")}
        >
          <img src={CloseIcon} alt="" />
        </button>
      </div>

      <div className={styles.modal_content}>
        <div className={styles.form_row}>
          <label htmlFor="section-name">{t("Name")}</label>
          <input
            id="section-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {nameError && <div className={styles.field_error}>{nameError}</div>}
        </div>

        <div className={cls(styles.button_group, styles.button_group_section)}>
          <button
            type="button"
            className={styles.secondary_button}
            onClick={onClose}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={styles.primary_button}
            disabled={trimmed.length === 0 || isDuplicate || isSaving}
            onClick={handleSave}
          >
            {isRename ? t("Save") : t("Add Section")}
          </button>
        </div>
      </div>
    </ModalLayout>
  );
};

export default SectionModal;
